import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { SessionStorageService } from '@df/browser/storage/session-storage.service';
import { Events } from '@df/core/service/event';
import { BodyClassService } from '@df/ui/body-class.service';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { CountdownComponent } from '@df/ui/display/countdown.component';
import { FadeSliderSlideComponent } from '@df/ui/fade-slider/fade-slider-slide.component';
import { FadeSliderComponent } from '@df/ui/fade-slider/fade-slider.component';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { UiComponent } from '@df/ui/ui.component';
import { GtmTrackClickDirective } from '../../../module/gtm/gtm-track-click.directive';
import { PageHeaderComponent } from '../header/page-header.component';
import { NPageHeader } from '../header/page-header.interface';

/**
 * Promo bar component.
 *
 * @usage
 * ```html
 * <promo-bar></promo-bar>
 * ```
 */
@Component({
    selector: 'promo-bar',
    templateUrl: './promo-bar.html',
    styleUrl: './promo-bar.scss',
    standalone: true,
    imports: [
        CommonModule,
        CountdownComponent,
        FadeSliderComponent,
        FadeSliderSlideComponent,
        GtmTrackClickDirective,
        HrefComponent,
        TranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PromoBarComponent extends UiComponent implements OnInit, OnDestroy {
    /**
     * Promo Bar data
     */
    @Input({ required: true })
    data!: NPageHeader.IPageHeaderPromoBarData;

    /**
     * If hidden check
     */
    hidden = false;

    /**
     * If hide-up animation can be applied
     * it cannot be applied if bar is initially closed (not to appar cloasing on load)
     */
    canAnimate = false;

    /**
     * If is hidden
     */
    private readonly storageId = 'promoBarHidden';

    protected events = inject(Events);
    protected bodyClassService = inject(BodyClassService);
    protected pageHeaderComponent = inject(PageHeaderComponent);
    protected elementRef = inject(ElementRef);
    protected sessionStorageService = inject(SessionStorageService);

    override ngOnInit() {
        super.ngOnInit();

        this.hidden = !this.canShow();

        if (!this.hidden) {
            this.canAnimate = true;
            this.bodyClassService.add('promo-bar-open', this);
        }

        this.pageHeaderComponent.toggleCmsNode(this.elementRef.nativeElement, true);

        this.subscriptions.add(
            this.pageHeaderComponent.hasContent$.subscribe(() => {
                this.detectChanges();
            })
        );
    }

    override ngOnDestroy() {
        this.bodyClassService.remove('promo-bar-open', this);
        this.subscriptions.unsubscribe();
        this.pageHeaderComponent.toggleCmsNode(this.elementRef.nativeElement, false);

        super.ngOnDestroy();
    }

    hide() {
        this.sessionStorageService.set(this.storageId, 1);
        this.hidden = true;
        this.bodyClassService.remove('promo-bar-open', this);
        this.pageHeaderComponent.toggleCmsNode(this.elementRef.nativeElement, false);
        this.detectChanges();

        this.timeoutService.setTimeout(() => {
            this.events.broadcast('sticky.update');
        }, 250);
    }

    private canShow(): boolean {
        return !this.sessionStorageService.get(this.storageId) || this.client.isCms || !this.data?.closeable;
    }
}
